import type { FC } from 'react';

import { Placeholder, SlotAddTiles, useContent, useImageMapper, useRendering, useSetting } from '@eneco/cms';
import type { ImageFormats } from '@eneco/cms-types';
import { useFormatter } from '@eneco/i18n';
import { Bleed, Box, Heading, Image, PageGrid, Stack, Text } from '@eneco/sparky';
import type { HeadingProps } from '@eneco/sparky/types';

import type { ArticleContent } from './article-content.types';
import { ARTICLE_SLOTS, resolveSlot } from './slots';

/**
 * The header image's crops, as Sitecore defines them for this field (its layout service sends the image
 * already cut into these). The Sanity adapter builds them from the original.
 */
const IMAGE_FORMATS: ImageFormats = {
  default: { width: 1024, height: 460 },
  small: { width: 1024, height: 460 },
  large: { width: 1920, height: 640 },
};

/**
 * `Article` block for the CMS-agnostic lane, serving **both** providers. An article page's header (hero
 * image, title, subtitle and byline) and its body, the `jss-article-main` slot, in legacy's page grid and
 * padded boxes.
 *
 * Two documented simplifications against the legacy component
 * (`libs/sitecore/components/content/src/Article`), both forced by the `lib:cms-components` module
 * boundary (`nx.json`) rather than chosen:
 *
 * - **No `@eneco/custom-components/Article` layout.** That lib is out of boundary, so on desktop the
 *   heading sits above the body as legacy places it on mobile, instead of overlapping the image.
 * - **No tags.** The legacy component reads them from the Sitecore route's own fields via
 *   `useLayoutData`; a Sanity page carries no equivalent, so there is nothing to read.
 */

const Article: FC = () => {
  const content = useContent<ArticleContent>();
  const mapImage = useImageMapper();
  const mainSlot = resolveSlot(useRendering(), ARTICLE_SLOTS.main);

  const headingLevel = (useSetting('headingLevel') || 'h1') as HeadingProps['as'];
  const image = mapImage(content.image, IMAGE_FORMATS);
  const { date } = useFormatter();
  // As legacy: the byline's template with the author and the date filled in, and only with both.
  const byline =
    content.author && isDate(content.publicationDate)
      ? content.bylineText?.replace('{author}', content.author).replace('{date}', date.long(content.publicationDate))
      : undefined;

  return (
    <PageGrid as="article">
      <PageGrid.Item gridColumn={{ initial: '1/-1' }}>
        {image?.src && (
          <Bleed horizontal={{ initial: 'gridGutter', xl: 0 }}>
            <Image
              src={image.src}
              sources={image.sources}
              alt={image.alt ?? ''}
              width="100%"
              aspectRatio={image.aspectRatio}
              maxHeight="640px"
              objectFit="cover"
              hasLazyLoad={false}
            />
          </Bleed>
        )}
        <Box paddingX={{ lg: '16', xl: '32' }}>
          <Box paddingX={{ md: '16' }} paddingY="10">
            <Stack gap="10">
              <Stack gap="5">
                {content.title && (
                  <Heading as={headingLevel} size={{ initial: 'M', md: 'XL' }}>
                    {content.title}
                  </Heading>
                )}
                {content.subTitle && <Text size={{ initial: 'BodyM', md: 'BodyL' }}>{content.subTitle}</Text>}
                {byline && (
                  <Text size={{ initial: 'BodyM', md: 'BodyL' }} color="textLowEmphasis">
                    {byline}
                  </Text>
                )}
              </Stack>
              {mainSlot ? <Placeholder name={mainSlot} /> : null}
            </Stack>
          </Box>
        </Box>
        <SlotAddTiles slots={ARTICLE_SLOTS.main} />
      </PageGrid.Item>
    </PageGrid>
  );
};

/** A date that does not parse counts as none, where legacy's formatter would throw on it. */
const isDate = (value: string | undefined): value is string =>
  Boolean(value) && !Number.isNaN(new Date(value as string).getTime());

export default Article;
